import { useEffect, useState } from "react";
import { useUser } from "@/hooks/useUser";
import { useDashboardUserId } from "@/hooks/dashboard-queries";
import { bookingsApi } from "@/lib/bookings-api";

export type PayoutMethod = "connect" | "payout_link" | "manual" | "awaiting_setup";

export interface PayoutTierInfo {
  tier: number;
  payoutMethod: PayoutMethod;
  preferredPayoutMethod: "connect" | "payout_link" | "manual" | null;
  loading: boolean;
  error: string | null;
  /** Successful policy for this render's effective host and session. */
  resolved: boolean;
}

const unresolvedPolicy = (loading: boolean): PayoutTierInfo => ({
  tier: 0,
  payoutMethod: "awaiting_setup",
  preferredPayoutMethod: null,
  loading,
  error: null,
  resolved: false,
});

/**
 * Fetches the host's payout policy and exposes the numeric tier.
 *
 * Tier semantics (mirrors `eligibleRailsForTier` on AccountPayoutsPage and
 * the server-side payout-policy decision):
 *   1 → Stripe Connect only
 *   2 → Stripe Connect only (payout-link fallback retired 2026-07-14)
 *   3 → Payout link only (legacy — no market resolves here since the
 *       link rail was retired; kept for existing link state)
 *   4 → No payout rails available (e.g. account paused / restricted).
 *       UI should hide the Earnings tab and any payout setup CTAs.
 */
export function usePayoutTier(): PayoutTierInfo {
  const { session } = useUser();
  const token = session?.access_token;
  const userId = useDashboardUserId();
  const enabled = !!token && !!userId;
  const [state, setState] = useState<{
    token: string | undefined;
    userId: string | null;
    policy: PayoutTierInfo;
  }>(() => ({ token, userId, policy: unresolvedPolicy(enabled) }));

  useEffect(() => {
    setState({ token, userId, policy: unresolvedPolicy(enabled) });
    if (!token || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const policy = await bookingsApi.getMyPayoutPolicy(token);
        if (cancelled) return;
        setState({
          token,
          userId,
          policy: {
            tier: policy.decision.tier ?? 0,
            payoutMethod: policy.decision.payoutMethod,
            preferredPayoutMethod: policy.preferredPayoutMethod,
            loading: false,
            error: null,
            resolved: true,
          },
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          token,
          userId,
          policy: {
            ...unresolvedPolicy(false),
            error: err instanceof Error ? err.message : "Failed to load payout policy",
          },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, userId, enabled]);

  // Effects run after rendering: mask the old host synchronously, including
  // the first frame of same-token impersonation changes and logout.
  return state.token === token && state.userId === userId
    ? state.policy
    : unresolvedPolicy(enabled);
}
